import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { customerPaymentOrBookingGrip, getExpressSettingsApi } from "../../../../api";
import { getDubaiDate } from "../../../../utils/date";
import { useBooking } from "../../../../context/BookingContext";
import PayInStoreButton from "../../../PayInStore/PayInStoreButton";
import useAuthGuard from "../../../../hooks/useAuthGuard";
import termsAndConditions from "../../../../Constants/termsAndConditions";
import modalStyles from "../../../../Constants/TermsModal.module.css";
import styles from "./GripBookingFinal.module.css";

const REG_FEE = 20;
const EXP_FEE = 30;

const GripBookingFinal = () => {
  useAuthGuard();
  const { pickupService, address: contextAddress, customer } = useBooking();
  const navigate = useNavigate();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState("");
  const termsRef = useRef(null);
  const [showingToast, setShowingToast] = useState(false);
  const clearErrTimerRef = useRef(null);
  const [newRacketName, setNewRacketName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  // const [editModalOpen, setEditModalOpen] = useState(false);
  const [isBreakdownPopupOpen, setIsBreakdownPopupOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const redirectTimerRef = useRef(null);

  const {
    customerId,
    services = "Grip Service",
    bookingDate = "Not Provided",
    previousBookings = [],
    customerName: passedCustomerName,
    instoreLocation = null,
    address: passedAddress = null,
  } = location.state || {};

  // Use passed address from navigation state, fallback to context address
  const address = passedAddress || contextAddress;

  const customerName =
    passedCustomerName ||
    (customer ? `${customer.first_name} ${customer.last_name}` : "N/A");

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("bookings") || "[]");
      console.log("[GripBookingFinal:init] location.state:", location.state);
      console.log("[GripBookingFinal:init] stored bookings:", stored);
      console.log(
        "[GripBookingFinal:init] previousBookings (prop):",
        previousBookings
      );
      console.log("[GripBookingFinal:init] pickupService/address:", {
        pickupService,
        address,
      });
    } catch (e) {
      console.warn("[GripBookingFinal:init] parse error:", e);
    }
  }, []);

  const [expressSettings, setExpressSettings] = useState({
    is_express_enabled: true,
    marina_express_enabled: false,
    cutoff_hour: 17,
  });

  useEffect(() => {
    getExpressSettingsApi()
      .then((res) => {
        if (res?.data) setExpressSettings(res.data);
      })
      .catch((err) => {
        console.error("Could not fetch express settings in GripBookingFinal:", err);
      });
  }, []);

  const formatPickupService = (service) => {
    switch (service) {
      case "pickup":
        return "Pickup Only";
      case "delivery":
        return "Delivery Only";
      case "pickup/delivery":
        return "Both Pickup & Delivery";
      case "in-store":
        return "In-Store (No Charge)";
      case "express-pickup":
        return "Express Pickup";
      case "express-delivery":
        return "Express Delivery";
      case "express-pickup/delivery":
        return "Express Pickup & Delivery";
      default:
        return service || "in-store";
    }
  };

  const [bookings, setBookings] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("bookings") || "[]");
      return stored.length ? stored : previousBookings;
    } catch {
      return previousBookings;
    }
  });

  const lockedGripType = useMemo(() => {
    const b = bookings.find((x) => x?.gripType);
    return b?.gripType || null;
  }, [bookings]);

  const getGripCost = (b) => Number(b.gripCost ?? 0);
  const getServiceFee = (b) =>
    Number(b.serviceFee ?? (b.gripType === "Express" ? EXP_FEE : REG_FEE));
  const getIndividualCost = (b) =>
    Number(b.gripPrice ?? getGripCost(b) + getServiceFee(b));

  const breakdown = useMemo(() => {
    const itemsTotal = bookings.reduce(
      (acc, b) => acc + getIndividualCost(b),
      0
    );

    let pickupFee = 0;
    if (pickupService === "pickup" || pickupService === "delivery")
      pickupFee = 30;
    else if (pickupService === "pickup/delivery") pickupFee = 60;
    else if (
      pickupService === "express-pickup" ||
      pickupService === "express-delivery"
    )
      pickupFee = 50;
    else if (pickupService === "express-pickup/delivery") pickupFee = 100;

    return { itemsTotal, pickupFee, grandTotal: itemsTotal + pickupFee };
  }, [bookings, pickupService]);

  const overallTotal = breakdown.grandTotal;

  useEffect(() => {
    localStorage.setItem("bookings", JSON.stringify(bookings));
  }, [bookings]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
      if (clearErrTimerRef.current) {
        clearTimeout(clearErrTimerRef.current);
      }
    };
  }, []);

  const showError = (msg, { focusTerms = false } = {}) => {
    setErrorMessage(msg);
    setShowingToast(true);
    if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
    clearErrTimerRef.current = setTimeout(() => {
      setShowingToast(false);
      setErrorMessage("");
    }, 3200);
    if (focusTerms && termsRef.current) {
      termsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      termsRef.current.classList.add("pulseHighlight");
      setTimeout(
        () => termsRef.current?.classList.remove("pulseHighlight"),
        1600
      );
    }
  };

  // const handleEdit = (index) => {
  //   const bookingToEdit = bookings[index];
  //   setSelectedBookingIndex(index);
  //   setSelectedGripId(bookingToEdit.gripId || null);
  //   setSelectedGripType(bookingToEdit.gripType || "");
  //   setTotalCost(getIndividualCost(bookingToEdit) || 0);
  //   // setEditModalOpen(true);
  // };

  const handleDelete = (index) => {
    const updated = bookings.filter((_, i) => i !== index);
    setBookings(updated);
    localStorage.setItem("bookings", JSON.stringify(updated));
  };

  const handleAddMore = () => {
    if (!newRacketName.trim()) {
      showError("Please enter a racket name before adding more.");
      return;
    }
    navigate("/customer/dashboard/grip-services", {
      state: {
        customerId,
        previousBookings: bookings,
        customerName,
        bookingDate,
        pickupService,
        instoreLocation,
        services,
        racketName: newRacketName,
        isAddMore: true,
        stringTypeConstraint: lockedGripType,
        address,
        lockServiceSelection: true,
      },
    });
  };

  const generateUniqueCartId = () => {
    const prefix = "TXN";
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1000);
    return `${prefix}-${timestamp}-${randomPart}`;
  };

  const customerEmail =
    location.state?.customerEmail ??
    customer?.email ??
    customer?.email_address ??
    customer?.primary_email ??
    null;

  const handleProceedToPay = async () => {
    if (!agreedToTerms) {
      showError("Please agree to the Terms & Conditions before proceeding.");
      return;
    }
    if (bookings.length === 0) {
      showError("No bookings to process.");
      return;
    }

    setIsProcessing(true);

    for (const b of bookings) {
      if (!b.gripId) {
        showError("Please select a grip for all bookings.");
        setIsProcessing(false);
        return;
      }
      if (!b.gripType) {
        showError(
          "Please select a service type (Regular/Express) for all bookings."
        );
        setIsProcessing(false);
        return;
      }
    }

    // Validate Express availability (Cutoff time in Dubai & Marina restriction)
    const { ymd: todayDubai, hour: currentHourDubai } = getDubaiDate();
    const cutoffHour = expressSettings.cutoff_hour ?? 17;
    const isPastCutoff = (bookingDate === todayDubai || !bookingDate) && currentHourDubai >= cutoffHour;
    const isMarina = pickupService === "in-store" && instoreLocation?.toLowerCase().includes("marina");
    const isDubaiHills = pickupService === "in-store" && instoreLocation?.toLowerCase().includes("dubai");

    for (const b of bookings) {
      if (String(b.gripType || "").toLowerCase() === "express") {
        if (!expressSettings.is_express_enabled) {
          showError("Express service is currently disabled. Please select Regular speed.");
          setIsProcessing(false);
          return;
        }
        if (isMarina && !expressSettings.marina_express_enabled) {
          showError("Express service is not available at Marina location. Please select Regular speed.");
          setIsProcessing(false);
          return;
        }
        if (isDubaiHills && !expressSettings.dubaihills_express_enabled) {
          showError("Express service is not available at Dubai Hills location. Please select Regular speed.");
          setIsProcessing(false);
          return;
        }
        if (isPastCutoff) {
          const formattedHour = cutoffHour > 12 ? `${cutoffHour - 12}:00 PM` : `${cutoffHour}:00 AM`;
          showError(`Express service cutoff (${formattedHour} UAE time) has passed for today. Please select Regular speed.`);
          setIsProcessing(false);
          return;
        }
      }
    }

    const cartId = generateUniqueCartId();

    const bookingRequests = bookings.map((b) => {
      const _gripCost = getGripCost(b);
      const _serviceFee = getServiceFee(b);
      const _total = getIndividualCost(b);

      return {
        bigcommerce_customer_id: customerId,
        booking_date: bookingDate,
        pickup_option: pickupService,
        delivery_address: address || null,
        racket_name: b.racketName,

        grip_cost: Number(_gripCost), // BC grip price (0 for BYOG)
        service_fee: Number(_serviceFee), // 20 or 30
        total_cost: Number(_total), // = grip_cost + service_fee

        // cart + flow
        cart_id: cartId,
        freeService: false,
        instore_location: pickupService === "in-store" ? instoreLocation : null,

        // identification
        service_kind: "grip",
        service_speed: b.gripType, // Regular | Express
        grip_id: b.gripId ?? null,

        // BC traceability
        bc_product_id: b.bc_product_id ?? b.product_id ?? null,
        bc_variant_id: b.bc_variant_id ?? b.variant_id ?? null,
        bc_name: b.gripName ?? null,
        bc_sku: b.bc_sku ?? b.sku ?? null,
        bc_image_url: b.bc_image_url ?? b.image ?? null,

        bc_price_inc_tax: Number(_gripCost),
        // customer identity (helps email receipts)
        customer_name: customerName,
        customer_email: customerEmail,
      };
    });

    console.log("[GripBookingFinal:submit] bookingRequests:", bookingRequests);
    console.log(
      "[GripBookingFinal:submit] pickup_fee:",
      breakdown?.pickupFee || 0
    );

    try {
      const response = await customerPaymentOrBookingGrip({
        bookings: bookingRequests,
        pickup_fee: breakdown?.pickupFee || 0,
      });

      console.log(
        "[GripBookingFinal:submit] response:",
        response?.status,
        response?.data
      );

      const data = response.data;

      if (response.status === 200 || response.status === 201) {
        if (data.bookings && data.bookings.length > 0) {
          console.log(
            "[GripBookingFinal] booking-only flow (no payment). Created:",
            data.bookings
          );
          alert("Your booking has been created successfully!");
          setBookings([]);
          localStorage.removeItem("bookings");

          navigate("/grip-payment-success?freeBooking=1", {
            state: {
              bookingIds: data.bookings.map((b) => b.booking_id),
              customerName,
              totalCost: 0,
              message: "Your booking has been successfully created.",
            },
          });
        } else if (data.payment_url) {
          console.log(
            "[GripBookingFinal] redirecting to payment:",
            data.payment_url
          );
          window.location.assign(data.payment_url);
        } else {
          console.error(
            "[GripBookingFinal] Unknown success response shape:",
            data
          );
          showError("Something went wrong!");
        }
      } else {
        console.error("Error creating booking/payment:", data);
        showError("Failed to create booking or process payment.");
      }
    } catch (error) {
      console.error("Booking Submission Error:", error);
      showError("Error processing bookings.");
    } finally {
      setIsProcessing(false);
    }
  };

  const openBreakdown = () => setIsBreakdownPopupOpen(true);

  return (
    <div className={styles.container}>
      {isProcessing && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loader}></div>
            <p>Please wait...</p>
            <p className="subText">Your booking is being processed.</p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div
          className={[
            styles.errorMessage,
            showingToast ? styles.toastVisible : null,
          ]
            .filter(Boolean)
            .join(" ")}
          role="status"
          aria-live="polite"
        >
          {errorMessage}
        </div>
      )}

      <h2 className={styles.title}>Booking Summary</h2>
      <p>
        <strong>Customer Name:</strong> {customerName || "N/A"}
      </p>
      <p>
        <strong>Booking Date:</strong> {bookingDate}
      </p>
      <p>
        <strong>Pickup Service:</strong>{" "}
        {formatPickupService(pickupService || "in-store")}
      </p>
      {pickupService === "in-store" && instoreLocation && (
        <p>
          <strong>Store Location:</strong> {instoreLocation}
        </p>
      )}
      {pickupService !== "in-store" && address && (
        <p>
          <strong>Address:</strong> {address}
        </p>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Racket Name</th>
              <th>Grip Name</th>
              <th>Service Type</th>
              <th>Total Cost</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length > 0 ? (
              bookings.map((booking, index) => {
                const lineTotal = getIndividualCost(booking);
                return (
                  <tr key={index}>
                    <td>{booking.racketName || "Not Provided"}</td>
                    <td>{booking.gripName || `Grip #${booking.gripId}`}</td>
                    <td>{booking.gripType || "Not Selected"}</td>
                    <td>
                      <span
                        title={`Grip: ${getGripCost(
                          booking
                        )} + Service: ${getServiceFee(booking)}`}
                      >
                        <span className="dirham-symbol">&#xea;</span>{" "}
                        {lineTotal.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      {/* <button
                        className={styles.editButton}
                        onClick={() => handleEdit(index)}
                      >
                        Edit
                      </button> */}
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(index)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className={styles.emptyMessage}>
                  No bookings created yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.overallCostBlock}>
        <span className={styles.overallCostText}>
          <strong>Overall Cost:</strong>{" "}
          <span className="dirham-symbol">&#xea;</span>{" "}
          {overallTotal.toFixed(2)}
        </span>
        <button className={styles.infoButton} onClick={openBreakdown}>
          ℹ
        </button>
      </div>

      {/* {isBreakdownPopupOpen && breakdown && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Cost Breakdown</h3>
            <p>
              <strong>Total Items:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.itemsTotal.toFixed(2)}
            </p>
            <p>
              <strong>Pickup/Delivery Fee:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.pickupFee.toFixed(2)}
            </p>
            <hr />
            <p>
              <strong>Grand Total:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.grandTotal.toFixed(2)}
            </p>
            <button
              className={styles.closeModalButton}
              onClick={() => setIsBreakdownPopupOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )} */}

      {isBreakdownPopupOpen && breakdown && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Cost Breakdown</h3>
              <button
                className={styles.iconCloseButton}
                onClick={() => setIsBreakdownPopupOpen(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className={styles.breakdownRow}>
              <span>Total Items:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.itemsTotal.toFixed(2)}
              </span>
            </div>

            <div className={styles.breakdownRow}>
              <span>Pickup/Delivery Fee:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.pickupFee.toFixed(2)}
              </span>
            </div>

            <hr className={styles.divider} />

            <div className={`${styles.breakdownRow} ${styles.grandTotalRow}`}>
              <span>Grand Total:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.grandTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {bookings.length > 0 && (
        <div className={styles.formGroup}>
          <input
            type="text"
            value={newRacketName}
            onChange={(e) => setNewRacketName(e.target.value)}
            className={styles.input}
            autoComplete="off"
            placeholder="Enter Racket Name"
            name="racketName"
          />
          <button className={styles.addMoreButton} onClick={handleAddMore}>
            Add More Grips
          </button>
        </div>
      )}

      <div id="terms-section" ref={termsRef} className={styles.ackActionsRow}>
        <label className={styles.termsLabel}>
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={() => setAgreedToTerms(!agreedToTerms)}
          />
          <span>
            I agree to the{" "}
            <button
              type="button"
              onClick={() => setShowTermsPopup(true)}
              className={styles.termsLink}
            >
              Terms & Conditions
            </button>
          </span>
        </label>

        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={() => navigate(-1)}>
            Cancel
          </button>

          {overallTotal > 0 && (
            <PayInStoreButton
              bookings={bookings}
              customerId={customerId}
              customerName={customerName}
              pickupService={pickupService}
              address={address}
              bookingDate={bookingDate}
              instoreLocation={instoreLocation}
              agreedToTerms={agreedToTerms}
              buttonClass={styles.outlineButton}
            />
          )}

          <button className={styles.proceedButton} onClick={handleProceedToPay}>
            {overallTotal === 0 ? "Confirm Booking" : "Proceed to Pay"}
          </button>
        </div>
      </div>

      {showTermsPopup && (
        <div className={modalStyles.termsModal}>
          <div className={modalStyles.termsModalContent}>
            <div dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
            <div className={modalStyles.termsModalFooter}>
              <button
                className={modalStyles.closeButton}
                onClick={() => setShowTermsPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GripBookingFinal;
